import { useStringParam } from '@/hooks/useParam';
import { fetchWithCancel } from '@/utils/fetchWithCancel';
import { VocabularyType } from '@/utils/suggestEndpoints';
import { truncate } from '@/utils/truncate';
import isUndefined from 'lodash/isUndefined';
import { useCallback } from 'react';
import { FormattedMessage, IntlShape } from 'react-intl';
import { longDateFormatProps } from '@/components/dateFormats';
import DisplayName, { DisplayNameGetDataProps } from './DisplayName';
import { useChecklistKey } from '@/hooks/useChecklistKey';
import { isFilterValueObject, isPresentBound } from './filterValue';

type LabelResult = { title: string; description?: string };

// utility function to generate label for range or equal filters
function rangeOrEqualLabel(
  path: string,
  formatter?: (value: string | number, intl: IntlShape) => string
) {
  const formatValue = formatter ?? ((value: string | number) => value);
  const getData = ({ id, intl }: { id: string | number | object; intl: IntlShape }) => {
    const value = isFilterValueObject(id) ? id : undefined;
    // translations only use the bounds that are present, so absent ones are left unformatted
    const format = (v: string | number | undefined) =>
      v === undefined ? undefined : formatValue(v, intl);
    if (value?.type === 'range') {
      let translationKey;
      const from = isPresentBound(value.value.gte) ? value.value.gte : value.value.gt;
      const to = isPresentBound(value.value.lte) ? value.value.lte : value.value.lt;
      if (isUndefined(from)) {
        translationKey = 'lt';
      } else if (isUndefined(to)) {
        translationKey = 'gt';
      } else {
        translationKey = 'between';
      }
      return (
        <FormattedMessage
          id={`${path}.${translationKey}`}
          defaultMessage={'Filter name'}
          values={{ from: format(from), to: format(to) }}
        />
      );
    } else if (value?.type === 'equals') {
      return (
        <FormattedMessage
          id={`${path}.e`}
          defaultMessage={'Filter name'}
          values={{ from: formatValue(value.value, intl), is: formatValue(value.value, intl) }}
        />
      );
    } else if (value?.type === 'greaterThanOrEquals') {
      return (
        <FormattedMessage
          id="intervals.description.gte"
          defaultMessage={`>= ${formatValue(value.value, intl)}`}
          values={{ from: formatValue(value.value, intl) }}
        />
      );
    } else if (value?.type === 'lessThanOrEquals') {
      return (
        <FormattedMessage
          id="intervals.description.lte"
          defaultMessage={`<= ${formatValue(value.value, intl)}`}
          values={{ to: formatValue(value.value, intl) }}
        />
      );
    } else if (value?.type === 'lessThan') {
      return (
        <FormattedMessage
          id="intervals.description.lt"
          defaultMessage={`< ${formatValue(value.value, intl)}`}
          values={{ to: formatValue(value.value, intl) }}
        />
      );
    } else if (value?.type === 'greaterThan') {
      return (
        <FormattedMessage
          id="intervals.description.gt"
          defaultMessage={`> ${formatValue(value.value, intl)}`}
          values={{ from: formatValue(value.value, intl) }}
        />
      );
    } else {
      return <FormattedMessage id={`invalidValue`} defaultMessage={'Invalid value'} />;
    }
  };
  return ({ id }: { id: string | number | object }) => {
    const fetch = useCallback(
      ({ id, intl }: DisplayNameGetDataProps) => ({
        promise: Promise.resolve({
          title: getData({ id, intl }),
        }),
      }),
      []
    );

    return <DisplayName getData={fetch} id={id} useHtml={false} />;
  };
}

export const WildcardLabel = ({ id }: { id: string | number | object }) => {
  const value = typeof id === 'object' && id !== null && 'value' in id ? id.value : id;
  if (typeof value !== 'string' && typeof value !== 'number') {
    return <span>Unknown</span>;
  }
  const stringValue = value.toString();
  const trimmed = stringValue.trim();
  const displayValue = trimmed.length !== stringValue.length ? `"${stringValue}"` : stringValue;

  if (isFilterValueObject(id) && id.type === 'like' && typeof value === 'string') {
    return <i>{displayValue}</i>;
  }

  return displayValue;
};

export const YearLabel = rangeOrEqualLabel('intervals.compactTime');
export const GeologicalTimeLabel = rangeOrEqualLabel('intervals.compactTime');
export const CoordinateUncertaintyLabel = rangeOrEqualLabel('intervals.compactMeters');
export const DecimalLatitudeLabel = rangeOrEqualLabel('intervals.description');
export const DecimalLongitudeLabel = rangeOrEqualLabel('intervals.description');
export const distanceFromCentroidInMetersLabel = rangeOrEqualLabel('intervals.compactMeters');
export const DepthLabel = rangeOrEqualLabel('intervals.compactMeters');
export const NucleotideSequenceLengthLabel = rangeOrEqualLabel('intervals.compactBasePairs');
export const ElevationLabel = rangeOrEqualLabel('intervals.compactMeters');
export const QuantityLabel = rangeOrEqualLabel('intervals.description');
export const OrganismQuantityLabel = rangeOrEqualLabel('intervals.description');
export const SampleSizeValueLabel = rangeOrEqualLabel('intervals.description');
export const RelativeOrganismQuantityLabel = rangeOrEqualLabel('intervals.description');
export const StartDayOfYearLabel = rangeOrEqualLabel('intervals.description');
export const EndDayOfYearLabel = rangeOrEqualLabel('intervals.description');
export const DateLabel = rangeOrEqualLabel('intervals.compactTime', (value, intl) => {
  const date = new Date(value);
  if (intl) {
    const stringDate = intl.formatDate(value, longDateFormatProps);
    return stringDate;
  }
  return date.toLocaleDateString('da-DK', longDateFormatProps);
});

function getEnumLabel({ template }: { template: (id: string) => string }) {
  return ({ id }: { id: string | number | object }) => {
    const getData = useCallback(
      ({ id }: DisplayNameGetDataProps) => ({
        promise: Promise.resolve({
          title: <FormattedMessage id={template(id.toString())} defaultMessage={id.toString()} />,
        }),
      }),
      []
    );

    return <DisplayName getData={getData} id={id} useHtml={false} />;
  };
}

export function PolygonLabel({ id }: { id: string | number | object }) {
  const getData = useCallback(({ id }: DisplayNameGetDataProps) => {
    const { promise, cancel } = fetchWithCancel(
      `${import.meta.env.PUBLIC_WEB_UTILS}/polygon-name?wkt=${id}`
    );
    return {
      promise: promise
        .then((response) => response.json())
        .then((data) => ({ title: truncate(data.title, 100) })),
      cancel,
    };
  }, []);

  return <DisplayName getData={getData} id={id} useHtml={false} />;
}

type GraphQlLabelResponse<TItem> = { data?: { item?: TItem | null } };

function getGraphQlLabel<TItem>({
  query,
  transform,
}: {
  query: string;
  transform?: (response: GraphQlLabelResponse<TItem>) => LabelResult;
}) {
  // an empty title renders as 'Unknown', same as a missing one
  const transformer =
    transform ??
    ((response: GraphQlLabelResponse<{ title?: string | null }>) => ({
      title: response?.data?.item?.title ?? '',
    }));
  return ({ id }: { id: string | number | object }) => {
    const getData = useCallback(({ id, config }: DisplayNameGetDataProps) => {
      const { promise, cancel } = fetchWithCancel(
        `${config.graphqlEndpoint}?query=${encodeURIComponent(
          query
        )}&variables=${encodeURIComponent(JSON.stringify({ key: id }))}`,
        {
          headers: {
            ...(typeof window !== 'undefined' ? { 'x-gbif-site-url': window.location.href } : {}),
          },
        }
      );
      return {
        promise: promise.then((response) => response.json()).then((json) => transformer(json)),
        cancel,
      };
    }, []);

    return <DisplayName getData={getData} id={id} useHtml={false} />;
  };
}

function getEndpointLabel<TResponse>({
  template,
  transform: transformer,
}: {
  template: ({ id, v1Endpoint }: { id: string | number | object; v1Endpoint: string }) => string;
  transform: (response: TResponse, props: DisplayNameGetDataProps) => LabelResult;
}) {
  return ({ id }: { id: string | number | object }) => {
    const getData = useCallback(({ id, intl, config, currentLocale }: DisplayNameGetDataProps) => {
      const endpoint = template({ id, v1Endpoint: config.v1Endpoint });
      const { promise, cancel } = fetchWithCancel(endpoint);
      return {
        promise: promise
          .then((response) => response.json())
          .then((response) => transformer(response, { id, intl, config, currentLocale })),
        cancel,
      };
    }, []);

    return <DisplayName getData={getData} id={id} useHtml={false} />;
  };
}

export function IdentityLabel({ id }: { id: string | number | object }) {
  const getData = useCallback(
    ({ id }: DisplayNameGetDataProps) => ({
      promise: Promise.resolve({ title: id.toString() }),
    }),
    []
  );
  return <DisplayName getData={getData} id={id} useHtml={false} />;
}

// a special taxonLabel that always use the defult checklistKey from the config
export function BackboneTaxonLabel({ id }: { id: string | number | object }) {
  return <TaxonLabel id={id} checklistKey={import.meta.env.PUBLIC_CLASSIC_BACKBONE_KEY} />;
}

export function TaxonLabel({
  id,
  checklistKey,
}: {
  id: string | number | object;
  checklistKey?: string;
}) {
  const defaultChecklistKey = useChecklistKey();
  const [urlChecklistKey] = useStringParam({
    key: 'checklistKey',
    defaultValue: undefined,
    hideDefault: false,
  });
  const getData = useCallback(
    ({ id, config }: DisplayNameGetDataProps) => {
      const variables = {
        checklistKey: checklistKey ?? urlChecklistKey ?? defaultChecklistKey,
      };
      const { promise, cancel } = fetchWithCancel(
        `${config.graphqlEndpoint}?variables=${encodeURIComponent(
          JSON.stringify(variables)
        )}&query=${encodeURIComponent(
          `query($checklistKey: ID) {
            taxon(datasetKey: $checklistKey, key:"${id}") {
              scientificName
            }
          }`
        )}`
      );
      return {
        promise: promise
          .then((response) => response.json())
          .then((response) => ({ title: response.data.taxon.scientificName })),
        cancel,
      };
    },
    [checklistKey, urlChecklistKey, defaultChecklistKey]
  );

  return <DisplayName useHtml getData={getData} id={id} />;
}

function getVocabularyLabel(
  result: VocabularyType,
  { currentLocale, id }: DisplayNameGetDataProps
) {
  const vocabularyLocale = currentLocale?.vocabularyLocale ?? currentLocale.code ?? 'en';

  // transform result labels to an object with language as keys
  const labels = result.label.reduce((acc: Record<string, string>, label) => {
    acc[label.language] = label.value;
    return acc;
  }, {});

  const title = labels[vocabularyLocale] || labels.en || result.name || id.toString();
  return { title };
}

export const ProgrammeLabel = getEnumLabel({ template: (id) => `enums.programme.${id}` });
export const LiteratureTypeLabel = getEnumLabel({ template: (id) => `enums.literatureType.${id}` });
export const LicenceLabel = getEnumLabel({ template: (id) => `enums.license.${id}` });
export const DatasetTypeLabel = getEnumLabel({ template: (id) => `enums.datasetType.${id}` });
export const TypeStatusLabel = getEnumLabel({ template: (id) => `enums.typeStatus.${id}` });
export const CountryLabel = getEnumLabel({ template: (id) => `enums.countryCode.${id}` });
export const RelevanceLabel = getEnumLabel({ template: (id) => `enums.relevance.${id}` });
export const TopicsLabel = getEnumLabel({ template: (id) => `enums.topics.${id}` });
export const PurposesLabel = getEnumLabel({ template: (id) => `enums.purposes.${id}` });
export const BasisOfRecordLabel = getEnumLabel({ template: (id) => `enums.basisOfRecord.${id}` });
export const MediaTypeLabel = getEnumLabel({ template: (id) => `enums.mediaType.${id}` });
export const MonthLabel = getEnumLabel({ template: (id) => `enums.month.${id}` });
export const DurationUnitLabel = getEnumLabel({ template: (id) => `enums.durationUnit.${id}` });
export const ContinentLabel = getEnumLabel({ template: (id) => `enums.continent.${id}` });
export const GbifRegionLabel = getEnumLabel({ template: (id) => `enums.gbifRegion.${id}` });
export const EndpointTypeLabel = getEnumLabel({ template: (id) => `enums.endpointType.${id}` });
export const DwcaExtensionLabel = getEnumLabel({ template: (id) => `enums.dwcaExtension.${id}` });
export const TaxonRankLabel = getEnumLabel({ template: (id) => `enums.taxonRank.${id}` });
export const TaxonStatusLabel = getEnumLabel({ template: (id) => `enums.taxonomicStatus.${id}` });
export const TaxonIssueLabel = getEnumLabel({ template: (id) => `enums.taxonIssue.${id}` });
export const IucnRedListCategoryLabel = getEnumLabel({
  template: (id) => `enums.iucnRedListCategory.${id}`,
});
export const occurrenceIssueLabel = getEnumLabel({
  template: (id) => `enums.occurrenceIssue.${id}`,
});
export const occurrenceStatusLabel = getEnumLabel({
  template: (id) => `enums.occurrenceStatus.${id}`,
});
export const booleanLabel = getEnumLabel({ template: (id) => `enums.yesNo.${id}` });

export const GadmGidLabel = getEndpointLabel({
  template: ({ id, v1Endpoint }) => `${v1Endpoint}/geocode/gadm/${id}`,
  transform: (response: { name?: string }) => ({ title: response?.name ?? '' }),
});

export const TypeStatusVocabularyLabel = getEndpointLabel({
  template: ({ id, v1Endpoint }) => `${v1Endpoint}/vocabularies/TypeStatus/concepts/${id}`,
  transform: getVocabularyLabel,
});

export const EstablishmentMeansLabel = getEndpointLabel({
  template: ({ id, v1Endpoint }) => `${v1Endpoint}/vocabularies/EstablishmentMeans/concepts/${id}`,
  transform: getVocabularyLabel,
});

export const DegreeOfEstablishmentLabel = getEndpointLabel({
  template: ({ id, v1Endpoint }) =>
    `${v1Endpoint}/vocabularies/DegreeOfEstablishment/concepts/${id}`,
  transform: getVocabularyLabel,
});

export const preservationTypeLabel = getEndpointLabel({
  template: ({ id, v1Endpoint }) => `${v1Endpoint}/vocabularies/PreservationType/concepts/${id}`,
  transform: getVocabularyLabel,
});

export const collectionContentTypeLabel = getEndpointLabel({
  template: ({ id, v1Endpoint }) =>
    `${v1Endpoint}/vocabularies/CollectionContentType/concepts/${id}`,
  transform: getVocabularyLabel,
});

export const datasetCategoryLabel = getEndpointLabel({
  template: ({ id, v1Endpoint }) => `${v1Endpoint}/vocabularies/DatasetCategory/concepts/${id}`,
  transform: getVocabularyLabel,
});

export const biomeTypeLabel = getEndpointLabel({
  template: ({ id, v1Endpoint }) => `${v1Endpoint}/vocabularies/BiomeType/concepts/${id}`,
  transform: getVocabularyLabel,
});

export const objectClassificationLabel = getEndpointLabel({
  template: ({ id, v1Endpoint }) =>
    `${v1Endpoint}/vocabularies/ObjectClassificationName/concepts/${id}`,
  transform: getVocabularyLabel,
});

export const InstitutionTypeLabel = getEndpointLabel({
  template: ({ id, v1Endpoint }) => `${v1Endpoint}/vocabularies/InstitutionType/concepts/${id}`,
  transform: getVocabularyLabel,
});

export const InstitutionDisciplineLabel = getEndpointLabel({
  template: ({ id, v1Endpoint }) => `${v1Endpoint}/vocabularies/Discipline/concepts/${id}`,
  transform: getVocabularyLabel,
});

export const PathwayLabel = getEndpointLabel({
  template: ({ id, v1Endpoint }) => `${v1Endpoint}/vocabularies/Pathway/concepts/${id}`,
  transform: getVocabularyLabel,
});

export const GeoTimeLabel = getEndpointLabel({
  template: ({ id, v1Endpoint }) => `${v1Endpoint}/vocabularies/GeoTime/concepts/${id}`,
  transform: getVocabularyLabel,
});

export const SexLabel = getEndpointLabel({
  template: ({ id, v1Endpoint }) => `${v1Endpoint}/vocabularies/Sex/concepts/${id}`,
  transform: getVocabularyLabel,
});

export const LifeStageLabel = getEndpointLabel({
  template: ({ id, v1Endpoint }) => `${v1Endpoint}/vocabularies/LifeStage/concepts/${id}`,
  transform: getVocabularyLabel,
});

export const TargetGeneLabel = getEndpointLabel({
  template: ({ id, v1Endpoint }) => `${v1Endpoint}/vocabularies/target_gene/concepts/${id}`,
  transform: getVocabularyLabel,
});

export const CollectionLabel = getGraphQlLabel({
  query: `query($key:ID!) {item:collection(key: $key) {title: name}}`,
});
export const InstitutionLabel = getGraphQlLabel({
  query: `query($key:ID!) {item:institution(key: $key) {title: name}}`,
});
export const DatasetLabel = getGraphQlLabel({
  query: `query($key:ID!) {item:dataset(key: $key) {title}}`,
});
export const PublisherLabel = getGraphQlLabel({
  query: `query($key:ID!) {item:organization(key: $key) {title}}`,
});
export const NetworkLabel = getGraphQlLabel({
  query: `query($key:ID!) {item:network(key: $key) {title}}`,
});
export const InstallationLabel = getGraphQlLabel({
  query: `query($key:ID!) {item:installation(key: $key) {title}}`,
});
export const TaxonKeyLabel = getGraphQlLabel({
  query: `query($key:ID!) {item:taxon(key: $key) {scientificName}}`,
  transform: (response: GraphQlLabelResponse<{ scientificName?: string | null }>) => ({
    title: response?.data?.item?.scientificName ?? '',
  }),
});

export function prettifyEnum(text: string) {
  return typeof text === 'string'
    ? text.charAt(0) + text.slice(1).toLowerCase().replace(/_/g, ' ')
    : 'Unknown';
}
